import { Controller } from "@hotwired/stimulus";
import "./component.scss";

export default class extends Controller {
  static targets = ["iframe", "wrapper"];
  static values = {
    src: String,
    roundedClass: String,
    autoplay: { type: Boolean }
  };

  // To control video playback from outside use stimulus outlets (e.g., bunny-connect)
  // compare gallery/component.js or use bunny_connect_controller.js

  connect() {
    if (!window.playerjs?.Player) {
      console.error("❌ playerjs.Player nicht gefunden!");
      return;
    }

    this.boundOnReady = this.onReady.bind(this);
    // this.boundOnPlay = this.onPlay.bind(this);
    this.boundOnEnded = this.onEnded.bind(this);

    this.roundedClass = this.roundedClassValue;
    this.playerInitialized = false;
    this.shouldPlay = this.autoplayValue;

    this.boundInitializePlayer = this.initializePlayer.bind(this);

    // Create the player as soon as possible
    this.boundInitializePlayer();

    // Fallbacks for edge cases (e.g. if connect happens before DOM is ready)
    document.addEventListener("DOMContentLoaded", this.boundInitializePlayer, { once: true });
    document.addEventListener("turbo:load", this.boundInitializePlayer, { once: true });
  }


  disconnect() {
    document.removeEventListener('DOMContentLoaded', this.boundInitializePlayer);
    document.removeEventListener('turbo:load', this.boundInitializePlayer);

    if (this.player) {
      try {
        // this.player.off("play",  this.boundOnPlay);
        this.player.off("ended", this.boundOnEnded);
        this.player.off("ready", this.boundOnReady);
      } catch (error) {
        // Silently ignore errors during cleanup
      }
      this.player = null;
    }

    this.playerInitialized = false;
    this.shouldPlay = false;

    // If something is waiting on readiness while we disconnect, unblock it.
    if (this._rejectReady) {
      this._rejectReady(new Error("Bunny Player disconnected before ready"));
    }

    this.readyPromise = null;
    this._resolveReady = null;
    this._rejectReady = null;
  }

  initializePlayer() {
    // Prevent double initialization
    if (this.playerInitialized || this.player) {
      return;
    }

    // Promise that resolves once the underlying player is actually ready
    this.readyPromise = new Promise((resolve, reject) => {
      this._resolveReady = resolve;
      this._rejectReady = reject;
    });

    // Fail fast if the embed never becomes ready (prevents hung promises)
    this.readyTimeoutId = window.setTimeout(() => {
      if (!this.player?.isReady) {
        const err = new Error("Bunny Player ready timeout");
        console.warn(err.message);
        if (this._rejectReady) this._rejectReady(err);
      }
    }, 15000);

    const iframe = this.buildIframe();

    this.player = new window.playerjs.Player(iframe);
    this.playerInitialized = true;

    // Resolve readiness via the official ready event
    this.player.on("ready", () => {
      if (this.readyTimeoutId) {
        window.clearTimeout(this.readyTimeoutId);
        this.readyTimeoutId = null;
      }
      this.boundOnReady();
      if (this._resolveReady) this._resolveReady(this.player);
    });
  }

  buildIframe() {
    const iframe = document.createElement("iframe");
    iframe.src = this.srcValue;
    iframe.style.border = "0";
    iframe.style.position = "absolute";
    iframe.style.top = "0";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.allow = "autoplay; fullscreen;";

    this.element.appendChild(iframe);

    const butt = document.createElement("button");
    butt.innerText = "Test Button";
    this.element.appendChild(butt);

    return iframe;
  }

  onReady() {
    // Double-check iframe is still connected before proceeding
    const iframe = this.element.querySelector('iframe');
    if (!iframe.isConnected || !this.player) {
      console.warn("Bunny Player: iframe disconnected during ready event");
      return;
    }

    this.player.on("ended", this.boundOnEnded);
    // this.player.on("play", this.boundOnPlay);

    if (this.shouldPlay) {
      // autoplay only muted
      this.mute();
      this.play();
    }
  }

  // onPlay() {
  //   this.wrapperTarget.classList.remove(this.roundedClass);
  // }

  onEnded() {
    this.shouldPlay = false;

    // this.wrapperTarget.classList.add(this.roundedClass);
    this.element.dispatchEvent(new CustomEvent('bunny-player-ended', { detail: { player: this }, bubbles: true }));
  }

  ensureReady() {
    if (!this.playerInitialized || !this.player) {
      this.initializePlayer();
    }

    // If initializePlayer() was short-circuited, readyPromise might not exist yet
    if (!this.readyPromise) {
      this.readyPromise = new Promise((resolve, reject) => {
        this._resolveReady = resolve;
        this._rejectReady = reject;
      });

      // If the player is already ready, resolve immediately
      if (this.player?.isReady) {
        this._resolveReady(this.player);
      } else {
        this.player?.on("ready", () => {
          if (this._resolveReady) this._resolveReady(this.player);
        });
      }
    }

    return this.readyPromise;
  }

  withReady(callback) {
    return this.ensureReady()
      .then((player) => {
        // Controller might have been disconnected while waiting
        if (!player || !this.player) return;
        return callback(player);
      })
      .catch((error) => {
        // Avoid noisy unhandled rejections; log once for visibility
        console.warn("Bunny Player: readiness failed", error);
      });
  }

  play() {
    return this.withReady((player) => player.play());
  }

  pause() {
    return this.withReady((player) => player.pause());
  }

  replay() {
    return this.withReady((player) => {
      player.setCurrentTime(0);
      player.play();
    });
  }

  toggleMute(callback) {
    return this.withReady((player) => {
      player.getMuted((muted) => {
        if (muted) {
          player.unmute();
        } else {
          player.mute();
        }

        // return !muted to callback, since we just toggled it
        if (callback) callback(!muted);
      });
    });
  }

  mute() {
    return this.withReady((player) => player.mute());
  }
}
